/* © CNES */
import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccessCollection } from '../../api/items/Items';
import { ConfigContext } from '../../context/ConfigContext';
import { AppHeader } from '../appHeader/AppHeader';
import { Feedback } from '../feedback/Feedback';
import { DownloadProgressDialog } from '../shared/Toast/DownloadProgressDialog';
import { JobsCompletedToast } from '../shared/Toast/JobsCompletedToast';
import { TooManyFeaturesToast } from '../shared/Toast/TooManyFeaturesToast';
import './Explore.scss';
import { LeftSidePanel } from './LeftSidePanel/LeftSidePanel';
import MapContainer from './mapContainer/MapContainer';
import { RightSidePanel } from './RightSidePanel/RightSidePanel';
import { GetStarted } from './startGuide/GetStarted';

/**
 * Explore displays whole Page for filters, results, map and items exploration.
 * @returns Element.
 */
export const Explore = () => {
  const [isFiltersVisible, setFiltersVisibility] = useState(true);
  const [maxFeatures] = useState(1000);
  const context = useContext(ConfigContext);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<
    { showSpinner: boolean; productId: string }[]
  >([]);
  const [isMaxProductsWarningVisible, setMaxProductsWarningVisible] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { data: userCollections, refetch: refetchUserCollections } = useQuery({
    queryKey: ['getAccessCollection', navigate, context.setError400],
    queryFn: getAccessCollection,
    refetchOnWindowFocus: false,
  });
  const [isDatalakeAvailable, setIsDatalakeAvailable] =
    useState<string>('loading');

  // Refetch user collections when the user logs in.
  useEffect(() => {
    if (!!context.userLoggedIn.token.value) {
      // timeout to prevent use of cached response from initial api call.
      setTimeout(() => {
        refetchUserCollections();
      }, 500);
    }
  }, [refetchUserCollections, context.userLoggedIn.token]);

  return (
    <>
      <AppHeader tabindex={0} />
      <div className="exploreContainer">
        <LeftSidePanel
          setFiltersVisibility={setFiltersVisibility}
          userCollections={userCollections || { collections: [] }}
          setIsDatalakeAvailable={setIsDatalakeAvailable}
          isDatalakeAvailable={isDatalakeAvailable}
          setMaxProductsWarningVisible={setMaxProductsWarningVisible}
        />
        <RightSidePanel
          setDownloadProgress={setDownloadProgress}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isDatalakeAvailable={isDatalakeAvailable}
          setMaxProductsWarningVisible={setMaxProductsWarningVisible}
        />
        <MapContainer
          isFiltersVisible={isFiltersVisible}
          maxFeatures={maxFeatures}
        />
        <Feedback />
        <GetStarted />
      </div>
      <JobsCompletedToast />
      <TooManyFeaturesToast
        isFiltersVisible={isFiltersVisible}
        maxFeatures={maxFeatures}
      />
      {context.isGetStarted && (
        <div
          className="floater-overlay"
          aria-label="floater-overlay"
          data-testid="floater-overlay"
          onClick={() => context.setIsGetStarted(false)}
        >
          <div className="overlay-text" aria-label="floater-text">
            Press anywhere to close
          </div>
        </div>
      )}
      <DownloadProgressDialog downloadProgress={downloadProgress} />
      <Dialog
        header="Cannot add products to My Data"
        visible={isMaxProductsWarningVisible}
        headerStyle={{ color: 'rgba(239, 74, 17, 1)' }}
        onHide={() => setMaxProductsWarningVisible(false)}
      >
        <p className="m-0 flex justify-content-center">
          You may not have more than 100 products at a time in My Data.
        </p>
      </Dialog>
    </>
  );
};
